import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAuthStore } from '../store/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredRole?: string; // Single required role (e.g., 'super_admin')
  requiredPermissions?: string[]; // New: permission-based access
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  requiredRole,
  requiredPermissions 
}) => {
  const { isAuthenticated, user, selectedClient } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check single required role first (simplest check)
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Result
          status="403"
          title="Access Denied"
          subTitle={`This page requires ${requiredRole} role.`}
          extra={
            <Button type="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  // Enhanced permission checking with dynamic role inclusion
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = user?.permissions || [];
    const userRole = user?.role;
    
    // Debug logging
    console.log('ProtectedRoute Debug:', {
      userRole: user?.role,
      userPermissions,
      requiredPermissions,
      hasAdminPermission: userPermissions.includes('admin'),
      isSuperAdmin: user?.role === 'super_admin',
      note: 'Checking both exact permissions (e.g., analytics:read) and legacy permissions (e.g., analytics)'
    });
    
    // Check if user has required permissions (highest priority - for custom roles)
    const hasSpecificPermission = requiredPermissions.some(permission => {
      // Check for exact permission match first
      if (userPermissions.includes(permission)) {
        return true;
      }
      
      // Check for legacy permission format (e.g., 'analytics' for 'analytics:read')
      const legacyPermission = permission.split(':')[0];
      if (userPermissions.includes(legacyPermission)) {
        return true;
      }
      
      return false;
    });
    
    console.log('Specific permission check:', {
      hasSpecificPermission,
      checkingPermissions: requiredPermissions.map(perm => {
        const legacyPerm = perm.split(':')[0];
        return {
          permission: perm,
          userHasExact: userPermissions.includes(perm),
          userHasLegacy: userPermissions.includes(legacyPerm),
          legacyPermission: legacyPerm
        };
      })
    });
    
    // If user has specific permission, grant access immediately (for custom roles)
    if (hasSpecificPermission) {
      console.log('✅ Navigation now uses permission-based visibility - custom roles will show appropriate tabs');
      // Don't block access, continue to render children
    } else {
      // Check if user has admin-level permissions (broad access)
      const hasAdminAccess = userPermissions.includes('admin') || userRole === 'super_admin';
      
      // Dynamic role inclusion: automatically allow admin and super_admin for most permissions
      const adminPermissions = [
        'user_management:read', 'user_management:write',
        'analytics:read', 'api_management:read', 'logs:read',
        'settings:write'
      ];
      const needsAdminAccess = requiredPermissions.some(perm => adminPermissions.includes(perm));
      const allowAdminRoles = needsAdminAccess && (userRole === 'admin' || userRole === 'super_admin');
      
      // Special case: client management is super_admin only
      const needsSuperAdminAccess = requiredPermissions.includes('client_management:read');
      const allowSuperAdminOnly = needsSuperAdminAccess && userRole === 'super_admin';
      
      const hasAccess = hasAdminAccess || allowAdminRoles || allowSuperAdminOnly;
       console.log('Admin access check:', {
        hasAdminAccess,
        needsAdminAccess,
        allowAdminRoles,
        needsSuperAdminAccess,
        allowSuperAdminOnly,
        hasAccess
      });

      if (!hasAccess) {
        return (
          <div style={{ 
            height: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Result
              status="403"
              title="Access Denied"
              subTitle="You don't have the required permissions to access this page."
              extra={
                <Button type="primary" onClick={() => window.history.back()}>
                  Go Back
                </Button>
              }
            />
          </div>
        );
      }
    }
  }
  // Fallback to role-based access if no permissions specified
  else if (allowedRoles && allowedRoles.length > 0 && user?.role) {
    if (!allowedRoles.includes(user.role)) {
      return (
        <div style={{ 
          height: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Result
            status="403"
            title="Access Denied"
            subTitle="You don't have permission to access this page."
            extra={
              <Button type="primary" onClick={() => window.history.back()}>
                Go Back
              </Button>
            }
          />
        </div>
      );
    }
  }

  // Super admins don't need client selection - they access super admin portal
  // Skip client selection redirect for super admins
  if (user?.role === 'super_admin') {
    return <>{children}</>;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
